import { SyntheticEvent, useState, FC, memo, useEffect } from 'react';
import classNames from 'classnames';

import MarketingAgreement from 'application/components/MarketingAgreement';
import { FORGOT_PAGE_PATH } from 'application/pages/EmailSendPage';
import { Link } from 'application/providers/RouterProvider';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';

import { Country } from 'domain/api/graphql/generated';

import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';
import PasswordInput from '@zvuk-b2b/react-uikit/ui/PasswordInput';
import PhoneInput from '@zvuk-b2b/react-uikit/ui/PhoneInput';
import Button from '@zvuk-b2b/react-uikit/ui/Button';

import { LegacyAuthFormProps } from './types';
import { isBelarusCountry } from './utils';
import { CookiePolicy } from './CookiePolicy';

import styles from './index.module.scss';

const LegacyAuthForm: FC<LegacyAuthFormProps> = ({
  isEmailDisabled = false,
  isPhoneAvailable = false,
  isRegisterMode = false,
  inputEmailLabel,
  inputEmailPlaceholder,
  inputPhoneLabel,
  inputPhonePlaceholder,
  inputPasswordLabel,
  inputPasswordPlaceholder,
  buttonContent,
  forgotLinkLabel,
  initData,
  className,
  isLoading,
  errors,
  onSubmit,
}) => {
  const [email, setEmail] = useState<string>(initData?.email || '');
  const [phone, setPhone] = useState<string>(initData?.phone || '');
  const [password, setPassword] = useState<string>('');
  const [marketingMaterialsAgreementReceived, setMarketingMaterialsAgreementReceived] = useState<boolean>(false);

  const isByCountry = isBelarusCountry(phone);

  const onRegisterClick = (e: SyntheticEvent) => {
    e.preventDefault();

    if (isPhoneAvailable) {
      onSubmit({
        email: email.trim(),
        phone: phone.trim(),
        password,
        marketingMaterialsAgreementReceived,
      });
    } else {
      onSubmit({
        email: email.trim(),
        password,
        marketingMaterialsAgreementReceived,
      });
    }
  };

  useEffect(() => {
    AnalyticsService.event(EventNameAMP.EMAIL_SIGNUP_PAGE_SHOWN);
  }, []);

  return (
    <form
      className={classNames(styles.form, className)}
      onSubmit={onRegisterClick}>
      <Input
        elementId="input_email"
        type={InputType.TEXT}
        value={email}
        error={errors?.email}
        isDisabled={isLoading || isEmailDisabled}
        containerClassName={styles.input}
        label={inputEmailLabel}
        placeholder={inputEmailPlaceholder}
        onChange={setEmail}
        onBlur={() => setEmail(email.trim())}
      />

      {isPhoneAvailable && (
        <PhoneInput
          elementId="input_tel"
          initialValue={phone}
          error={errors?.phone}
          isDisabled={isLoading}
          containerClassName={styles.input}
          label={inputPhoneLabel}
          placeholder={inputPhonePlaceholder}
          onChange={(value) => setPhone(value as string)}
          onBlur={() => setPhone(phone.trim())}
        />
      )}

      <PasswordInput
        elementId="input_password"
        value={password}
        isDisabled={isLoading}
        error={errors?.password}
        containerClassName={styles.passwordInput}
        label={inputPasswordLabel}
        placeholder={inputPasswordPlaceholder}
        onChange={setPassword}
        onBlur={() => setPassword(password.trim())}
      />

      {forgotLinkLabel && (
        <Link
          className={styles.link}
          to={FORGOT_PAGE_PATH}>
          {forgotLinkLabel}
        </Link>
      )}

      <Button
        elementId="button_register"
        isLoading={isLoading}
        onClick={onRegisterClick}
        className={styles.button}>
        {buttonContent}
      </Button>

      {isRegisterMode && !isByCountry && (
        <MarketingAgreement
          marketingMaterialsAgreementReceived={marketingMaterialsAgreementReceived}
          setMarketingMaterialsAgreementReceived={setMarketingMaterialsAgreementReceived}
          isLoading={isLoading}
          className={styles.checkbox}
        />
      )}

      {isByCountry && isRegisterMode && <CookiePolicy country={Country.By} />}

      <input
        type="submit"
        className={styles.hiddenInput}
      />
    </form>
  );
};

export default memo(LegacyAuthForm);
